import {
	Alert,
	Box,
	Chip,
	colors,
	designTokens,
	Panel,
	PanelBody,
	PanelHeader
} from "@itandi/itandi-bb-ui";
import Link from "next/link";
import { useRouter } from "next/router";
import type { ComponentProps } from "react";
import React from "react";
import { ConstructionConfirmedFooter } from "~/components/common/constructions/ConstructionConfirmedFooter";
import { ConstructionList } from "~/components/common/constructions/list/ConstructionList";
import { ConstructionTop } from "~/components/common/constructions/top/ConstructionTop";
import { DefaultV2 } from "~/components/common/layouts/DefaultV2";
import { DealLayout } from "~/components/common/layouts/deals/DealLayout";
import Loading from "~/components/common/parts/loading/Loading";
import { useDealEstimationHeaders } from "~/hooks/deals/useDealEstimationHeaders";
import { useDealEstimationHistories } from "~/hooks/deals/useDealEstimationHistories";
import { useDealEstimations } from "~/hooks/deals/useDealEstimations";
import { useDealConstructionItems } from "~/hooks/useDealConstructionItems";
import { useDeals } from "~/hooks/useDeals";
import { useIdQueryHandler } from "~/hooks/useIdQueryHandler";
import useItandiAccount from "~/hooks/useItandiAccount";
import type { NskResponse } from "~/models/Api";
import { estimationHeaderStatus } from "~/models/Deal";
import type {
	DealConstruction,
	DealConstructionContext
} from "~/models/DealConstruction";
import { estimationRequestSide } from "~/models/DealEstimationHeader";
import { getOriginVendor } from "~/models/DealVendorCompany";
import {
	IN_CONSTRUCTION,
	IN_ESTIMATION,
	isOwnStatus,
	OWN_SEND_ESTIMATE,
	SEND_ESTIMATE
} from "~/models/Status";
import { VENDOR_COMPANY_ITANDI_ID } from "~/models/VendorCompany";
import { PATH as DEALS_PATH } from "~/pages/deals";
import { PATH } from "~/pages/deals/[id]";
import { PATH as ESTIMATIONS_EDIT_PATH } from "~/pages/deals/[id]/estimations/edit";
import { update as updateStatus } from "~/utils/api/DealStatus";
import { update as updateEnabled } from "~/utils/api/deals/DealEstimation";
import { getDownloadPath } from "~/utils/api/deals/DealEstimationFile";
import {
	updateAccept,
	updateReject
} from "~/utils/api/deals/DealEstimationHeader";
import { getDownloadPath as getHistoryDownloadPath } from "~/utils/api/deals/DealEstimationHistoryFile";
import { isEmpty } from "~/utils/StringUtil";
import Styles from "./Page.module.scss";

// 下記ステータスの場合のみ施工対象を変更できる
const allowedStatuses: number[] = [SEND_ESTIMATE, OWN_SEND_ESTIMATE];

export const Page: React.FC = () => {
	// TODO: 環境変数かDBで管理する
	// https://itandi-dev.atlassian.net/browse/NSKDEV-1960
	const getEditableConstructionTargetVendorIds = (): number[] => {
		if (process.env.NEXT_PUBLIC_NSK_ENV !== "production") {
			return [
				VENDOR_COMPANY_ITANDI_ID, // イタンジ株式会社
				9 // 検証用
			];
		}
		return [
			VENDOR_COMPANY_ITANDI_ID, // イタンジ株式会社
			127, // 株式会社セイコー
			128, // 株式会社三和テレム
			159, // イケダ商事合同会社
			161, // 株式会社ハウスクリニック
			195, // 株式会社関東リノベーション
			196, // 株式会社トミーズマート
			197 // 株式会社ステップスリー
		];
	};
	const id = useIdQueryHandler();
	const router = useRouter();
	const { data: currentAccount } = useItandiAccount().currentAccount();
	const { data: estimationContext, mutate: mutateConstructions } =
		useDealEstimations().index(id);
	const { data: deal, mutate: mutateDeal } = useDeals().show(id);
	const { data: dealConstructionItems } = useDealConstructionItems();
	const { data: dealEstimationHistoryContext } =
		useDealEstimationHistories().index(id);
	const { data: dealEstimationHeaders, mutate: mutateHeaders } =
		useDealEstimationHeaders().index(id);
	const requestHeader =
		dealEstimationHeaders?.find((header) => header.status === "request") ??
		null;
	const { data: dealEstimationRequestHistoryContext, isLoading } =
		useDealEstimationHeaders().show(id, requestHeader && requestHeader.id);
	const isReadonly = React.useMemo(() => {
		if (deal === null) {
			return true;
		}
		if (requestHeader !== null) return true;
		return !allowedStatuses.some((s) => s === deal.statusId);
	}, [deal, requestHeader]);
	React.useEffect(() => {
		if (deal === null) return;
		if (!isOwnStatus(deal.statusId) && estimationRequestSide(deal, "client")) {
			router.replace(ESTIMATIONS_EDIT_PATH(deal.id)).then();
		}
	}, [deal, router]);
	if (
		id === null ||
		deal === null ||
		isLoading ||
		estimationContext === null ||
		dealEstimationHistoryContext === null ||
		dealConstructionItems === null ||
		dealEstimationHeaders === null ||
		currentAccount === null
	) {
		return <Loading isLoading />;
	}
	const getConstructionContextList = (): DealConstructionContext => {
		if (isOwnStatus(deal.statusId)) {
			if (
				dealEstimationHistoryContext &&
				dealEstimationHistoryContext?.dealConstructions.length !== 0
			) {
				return dealEstimationHistoryContext;
			}
			return estimationContext;
		}
		return dealEstimationRequestHistoryContext ?? estimationContext;
	};
	const handleMutateConstructions = async (): Promise<void> => {
		await mutateConstructions?.();
		await mutateHeaders?.();
		await mutateDeal?.();
	};
	const handleClickPreview = (): void => {
		const path = getDownloadPath(id);
		window.open(path);
	};
	const handleClickHistoryPreview = (): void => {
		if (requestHeader === null) {
			throw new Error("requestHeader cannot be null");
		}
		const path = getHistoryDownloadPath(deal.id, requestHeader.id);
		window.open(path);
	};
	const items = [
		{ as: Link, href: DEALS_PATH, label: "案件一覧" },
		{ as: Link, href: PATH(deal.id), label: "案件詳細" },
		{ label: "見積り確認" }
	] as const satisfies ComponentProps<typeof DealLayout>["items"];
	const handleUpdateEnabled = async (
		newConstruction: DealConstruction
	): Promise<void> => {
		if (newConstruction.type !== "DealEstimation") {
			return;
		}
		await updateEnabled(newConstruction, newConstruction.id, deal.id);
	};
	const handleAcceptUpdateStatus = async (): Promise<NskResponse> => {
		if (requestHeader === null) {
			throw new Error("requestHeader cannot be null");
		}
		return updateAccept(deal.id, requestHeader.id);
	};
	const handleUpdateRejectStatus = async (): Promise<NskResponse> => {
		if (requestHeader === null) {
			throw new Error("requestHeader cannot be null");
		}
		return updateReject(deal.id, requestHeader.id);
	};
	const handleAcceptForward = async (): Promise<NskResponse> =>
		updateStatus(deal.id, IN_CONSTRUCTION, null, null);
	const canEditConstructionEnabled = (): boolean => {
		const vendorCompany = getOriginVendor(deal);
		// 非共有案件は施工対象を変更できる
		if (vendorCompany === null) return true;

		return getEditableConstructionTargetVendorIds().includes(vendorCompany.id);
	};
	const estimationNote = requestHeader?.note ?? estimationContext.note;

	return (
		<DefaultV2>
			<DealLayout deal={deal} isVendor={false} items={items}>
				<div className={Styles.EstimateHeader}>
					<h1 className={Styles.EstimateTitle}>見積り確認</h1>
					{estimationHeaderStatus(deal).label !== "" && (
						<Chip
							background={estimationHeaderStatus(deal).color}
							borderColor={null}
							label={estimationHeaderStatus(deal).label}
							maxWidth="100%"
							textColor={colors["itandi-white"]}
						/>
					)}
				</div>
				{estimationHeaderStatus(deal).description !== "" &&
					deal.statusId !== IN_ESTIMATION && (
						<Box width="50%">
							<Alert>{estimationHeaderStatus(deal).description}</Alert>
						</Box>
					)}
				<ConstructionTop
					constructionContext={estimationContext}
					dealEstimationHistoryContext={dealEstimationRequestHistoryContext}
					handleClickEstimationHistoryPreview={
						requestHeader ? handleClickHistoryPreview : null
					}
					handleClickEstimationPreview={handleClickPreview}
					ownManagement={isOwnStatus(deal.statusId)}
					readonly={isReadonly}
				/>
				<ConstructionList
					companyType="client"
					constructionContext={getConstructionContextList()}
					deal={deal}
					dealConstructionItems={dealConstructionItems}
					docType="estimation"
					handleMutateConstructions={handleMutateConstructions}
					handleUpdateEnabled={
						canEditConstructionEnabled() ? handleUpdateEnabled : null
					}
					readonly={isReadonly}
					showEstimationConfirmedButton={
						(requestHeader && !isOwnStatus(deal.statusId)) ?? false
					}
					showHighlight={!isOwnStatus(deal.statusId)}
				/>
				{deal.estimationBy === "vendor" && (
					<Box mt={designTokens["spacing/l"]}>
						<Panel>
							<PanelHeader title="備考" />
							<div
								className={
									requestHeader?.noteModified ? Styles.ModifiedNote : undefined
								}
							>
								<PanelBody>
									{isEmpty(estimationNote) && requestHeader?.noteModified
										? "備考が削除されました。"
										: estimationNote}
								</PanelBody>
							</div>
						</Panel>
					</Box>
				)}
				<ConstructionConfirmedFooter
					account={currentAccount}
					companyType="client"
					deal={deal}
					dealEstimationContext={estimationContext}
					dealEstimationHeader={requestHeader}
					handleAcceptForward={handleAcceptForward}
					handleMutate={handleMutateConstructions}
					handleUpdateAcceptStatus={handleAcceptUpdateStatus}
					handleUpdateRejectStatus={handleUpdateRejectStatus}
					isAdmin={currentAccount.isAdmin}
				/>
			</DealLayout>
		</DefaultV2>
	);
};
